import { Text, useTranslations } from "ilenia";
import React from "react";

import Icons from "./icons";
import SearchInput from "./searchInput";
import {
  StyledBusinessUnitDropdown,
  StyledBusinessUnitDropdownList,
  StyledBusinessUnitDropdownItem,
  StyledCheckIcon,
  StyledBusinessUnitDropdownItemWrapper,
  StyledLockIcon,
  StyledSsoIcon,
} from "./styled";
import * as Types from "./types";

const MAX_UNITS_WITHOUT_SEARCH = 15;

const BusinessUnitDropdown: React.FC<Types.BusinessUnitDropdown> = ({
  changeSelectedUnit,
  selectedId,
  type,
  unitList,
  active,
}) => {
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [filteredUnitList, setFilteredUnitList] = React.useState(unitList);
  const [focusedItemIndex, setFocusedItemIndex] = React.useState(null);

  const [translations] = useTranslations();

  const filterUnitsList = React.useCallback(
    (value: string) => {
      const formattedValue = value.toLowerCase();
      const startingList = unitList.filter(({ name }) => name.toLowerCase().startsWith(formattedValue));
      const containingList = unitList.filter(({ name }) => name.toLowerCase().includes(formattedValue));
      const newList = [...startingList, ...containingList].reduce(
        (acc, cur) => (acc.some(({ id }) => cur.id === id) ? acc : [...acc, cur]),
        [],
      );
      setFilteredUnitList(newList);
    },
    [unitList],
  );

  const onSearchValueChange = React.useCallback(
    (e) => {
      const value = e.target.value;

      filterUnitsList(value);
      setSearchValue(value);

      if (filteredUnitList.length > 0) {
        setFocusedItemIndex(0);
      }
    },
    [type, filterUnitsList, filteredUnitList],
  );

  const setFocusedItem = React.useCallback(
    (increment: number) => {
      if (filteredUnitList.length === 0) {
        setFocusedItemIndex(null);
      } else if (focusedItemIndex === null) {
        setFocusedItemIndex(0);
      } else if (focusedItemIndex === 0 && increment === -1) {
        setFocusedItemIndex(filteredUnitList.length - 1);
      } else if (focusedItemIndex === filteredUnitList.length - 1 && increment === 1) {
        setFocusedItemIndex(0);
      } else if (focusedItemIndex >= 0 && focusedItemIndex <= filteredUnitList.length - 1) {
        setFocusedItemIndex(focusedItemIndex + increment);
      }
    },
    [filteredUnitList, focusedItemIndex],
  );

  const onSearchKeyUp = React.useCallback(
    (e: React.KeyboardEvent) => {
      e.preventDefault();
      const key = e.keyCode || e.code;

      switch (key) {
        case 38: // arrow up
          setFocusedItem(-1);
          break;
        case 40: // arrow down
          setFocusedItem(1);
          break;
        case 13: // Enter
          if (filteredUnitList.length > 0 && focusedItemIndex >= 0) {
            const unit = filteredUnitList[focusedItemIndex];
            if (unit) {
              changeSelectedUnit(unit, selectedId);
            }
          }
          break;
        default:
          return;
      }
    },
    [filteredUnitList, focusedItemIndex],
  );

  return (
    <StyledBusinessUnitDropdown active={active}>
      {unitList.length > MAX_UNITS_WITHOUT_SEARCH && (
        <div
          key="unitselectoritemsearch"
          role="button"
          onClick={(e) => {
            // stop event propagation to OutsideClickHandler:
            e.stopPropagation();
          }}
          tabIndex={0}
          onKeyUp={(e) => onSearchKeyUp(e)}
          aria-label={translations["ButtonAriaLabels.search"]}
        >
          <SearchInput type={type} searchValue={searchValue} onSearchValueChange={onSearchValueChange} />
        </div>
      )}
      <StyledBusinessUnitDropdownList>
        {filteredUnitList.map((unit, idx) => (
          <StyledBusinessUnitDropdownItem
            focused={idx === focusedItemIndex}
            selected={unit.id === selectedId}
            key={`unitselectoritem${unit.id}`}
            data-value={unit.id}
            onClick={() => changeSelectedUnit(unit, selectedId)}
          >
            <StyledCheckIcon>{unit.id === selectedId && <Icons.Check />}</StyledCheckIcon>
            <StyledBusinessUnitDropdownItemWrapper title={unit.name}>{unit.name}</StyledBusinessUnitDropdownItemWrapper>
            {unit.isItemLocked && (
              <StyledLockIcon>
                <Icons.Lock />
              </StyledLockIcon>
            )}
            {unit.ssoIsEnforced && (
              <StyledSsoIcon>
                <Text id={`BusinessAccountSelector.sso`} />
              </StyledSsoIcon>
            )}
          </StyledBusinessUnitDropdownItem>
        ))}
      </StyledBusinessUnitDropdownList>
    </StyledBusinessUnitDropdown>
  );
};

export default BusinessUnitDropdown;
